import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { fetchFearGreedIndex } from '../lib/coinglass';
import { FearGreedData } from '../types/coinglass';

const FearGreedIndex: React.FC = () => {
  const { data, error, isLoading, refetch } = useQuery<FearGreedData[]>({
    queryKey: ['fearGreedIndex'],
    queryFn: fetchFearGreedIndex,
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });

  const getColorByValue = (value: number) => {
    if (value <= 25) return 'text-red-500';
    if (value <= 45) return 'text-orange-500';
    if (value <= 55) return 'text-yellow-500';
    if (value <= 75) return 'text-green-500';
    return 'text-emerald-500';
  };

  const getBackgroundByValue = (value: number) => {
    if (value <= 25) return 'bg-red-500/20';
    if (value <= 45) return 'bg-orange-500/20';
    if (value <= 55) return 'bg-yellow-500/20';
    if (value <= 75) return 'bg-green-500/20';
    return 'bg-emerald-500/20';
  };

  if (error) return null;
  if (isLoading || !data) return null;

  const latestData = data?.[0];

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Fear & Greed Index</h3>
          <p className="text-sm text-gray-400">Market Sentiment</p>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {latestData ? (
        <div className="text-center">
          <div className={`text-6xl font-bold mb-2 ${getColorByValue(latestData.value)}`}>
            {latestData.value}
          </div>
          <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getBackgroundByValue(latestData.value)} ${getColorByValue(latestData.value)}`}>
            {latestData.valueClassification}
          </div>
          <div className="mt-4">
            <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${getColorByValue(latestData.value).replace('text-', 'bg-')}`}
                style={{ width: `${latestData.value}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Extreme Fear</span>
              <span>Extreme Greed</span>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-400">
            Last updated: {new Date(latestData.timestamp * 1000).toLocaleString()}
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-400">
          No data available
        </div>
      )}
    </div>
  );
};

export default FearGreedIndex;
